import { ActionsObservable, combineEpics, Epic, ofType, StateObservable } from 'redux-observable';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Actions, ActionTypes, IMapDataPayload, TAction } from '../Actions/Actions';
import { IGameState } from '../Store/store';
import { MissionState, MissionTypes } from '../reducers/missions';
import { MISSION_REFRESH_TIME } from '../../Config/Missions';
import BoardUtils from '../../Game/BoardUtils';
import { ItemIDs } from '../../Config/Items';

const initMissions: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, any>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.INIT_CORE, ActionTypes.CHANGE_VIEW),
        withLatestFrom(state$),
        filter(([_, state]) => state.StatePlayer.lastLevelIndex >= 10),
        map(([action, state]) => {
            const refresh =
                state.StateMissions.refreshTimer !== 0 &&
                Date.now() - state.StateMissions.refreshTimer >= MISSION_REFRESH_TIME;
            return Actions.missionInit([
                state.StateMissions.missions,
                state.StatePlayer.lastLevelIndex,
                refresh,
            ]);
        })
    );
};

const matchMission: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, any>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.MATCH_TILES),
        withLatestFrom(state$),
        filter(([_, state]) => state.StateLevel.matches.length > 0),
        mergeMap(([action, state]) => {
            const vals = state.StateLevel.matches.map(m =>
                BoardUtils.formatId(state.StateBoard.boardMap[m].tileType)
            );
            const matches = vals.map(t => BoardUtils.convertTileData(t));
            let matchMissions = state.StateMissions.missions.filter(
                v =>
                    (v.action.toLowerCase() === MissionTypes.MATCH ||
                        v.action.toLowerCase() === MissionTypes.MATCHGOLD) &&
                    v.missionState === MissionState.ACTIVE
            );

            if (matches[0].suit === 'gold') {
                matchMissions = matchMissions.filter(m => m.targetId === 'gold');
            } else {
                matchMissions = matchMissions.filter(m => m.targetId === 'pair');
            }

            if (matchMissions.length > 0) {
                return matchMissions.reduce((arr, mission) => {
                    arr.push(Actions.countForMission([mission.uid, 2]));
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

const collectStars: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, IMapDataPayload>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.UPDATE_MAP_DATA),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const starMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() === MissionTypes.COLLECT &&
                    v.targetId === 'stars' &&
                    v.missionState === MissionState.ACTIVE
            );

            const gainAmt = state.StatePlayer.gainedStars;

            let counts = [];
            if (starMissions.length > 0 && gainAmt > 0) {
                counts = starMissions.reduce((arr, mission) => {
                    arr.push(Actions.countForMission([mission.uid, gainAmt]));
                    return arr;
                }, []);
            }

            // counts.push(Actions.clearMissionStars());
            return counts;
        })
    );
};

const collectToys: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string[]>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.COUNT_OBJECTS),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const toyMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() === MissionTypes.COLLECT &&
                    v.targetId === 'toys' &&
                    v.missionState === MissionState.ACTIVE
            );

            if (toyMissions.length > 0) {
                return toyMissions.reduce((arr, mission) => {
                    arr.push(Actions.countForMission([mission.uid, action.payload.length]));
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

const completeLevels: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string[]>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.LEVEL_COMPLETE),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const completeLevelMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() === MissionTypes.COMPLETE &&
                    v.missionState === MissionState.ACTIVE
            );

            if (completeLevelMissions.length > 0) {
                return completeLevelMissions.reduce((arr, mission) => {
                    arr.push(Actions.countForMission([mission.uid, 1]));
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

const useHint: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.ACTIVATE_BOOSTER),
        withLatestFrom(state$),
        filter(([action]) => action.payload === ItemIDs.HINT),
        mergeMap(([action, state]) => {
            const hintMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() === MissionTypes.USE &&
                    v.targetId === ItemIDs.HINT &&
                    v.missionState === MissionState.ACTIVE
            );
            if (hintMissions.length > 0) {
                return hintMissions.reduce((arr, mission) => {
                    arr.push(Actions.countForMission([mission.uid, 1]));
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

const useFirecracker: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.ACTIVATE_BOOSTER),
        withLatestFrom(state$),
        filter(([action]) => action.payload === ItemIDs.FIRECRACKER),
        mergeMap(([action, state]) => {
            const hintMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() === MissionTypes.USE &&
                    v.targetId === ItemIDs.FIRECRACKER &&
                    v.missionState === MissionState.ACTIVE
            );
            if (hintMissions.length > 0) {
                return hintMissions.reduce((arr, mission) => {
                    arr.push(Actions.countForMission([mission.uid, 1]));
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

const useMagnet: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.ACTIVATE_BOOSTER),
        withLatestFrom(state$),
        filter(([action]) => action.payload === ItemIDs.MAGNET),
        mergeMap(([action, state]) => {
            const hintMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() === MissionTypes.USE &&
                    v.targetId === ItemIDs.MAGNET &&
                    v.missionState === MissionState.ACTIVE
            );
            if (hintMissions.length > 0) {
                return hintMissions.reduce((arr, mission) => {
                    arr.push(Actions.countForMission([mission.uid, 1]));
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

// update epic to trigger complete on counting mission progress
const updateMissions: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string[]>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.MISSION_COUNT),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const activeMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() !== MissionTypes.REACH &&
                    v.action.toLowerCase() !== MissionTypes.BEAT &&
                    v.missionState === MissionState.ACTIVE
            );

            if (activeMissions.length > 0) {
                return activeMissions.reduce((arr, mission) => {
                    if (mission.total >= mission.targetNum) {
                        arr.push(Actions.missionComplete(mission.uid));
                    }
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

// no count...just complete
// beat level
const beatLevel: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string[]>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.LEVEL_COMPLETE),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const beatLevelMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() === MissionTypes.BEAT &&
                    v.missionState === MissionState.ACTIVE
            );

            if (beatLevelMissions.length > 0) {
                return beatLevelMissions.reduce((arr, mission) => {
                    if (mission.targetNum === state.StateLevel.levelIndex) {
                        arr.push(Actions.missionComplete(mission.uid));
                    }
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

// reach combo
const reachCombo: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string[]>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.SET_MULTIPLIER),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const comboMissions = state.StateMissions.missions.filter(
                v =>
                    v.action.toLowerCase() === MissionTypes.REACH &&
                    v.missionState === MissionState.ACTIVE
            );

            if (comboMissions.length > 0) {
                return comboMissions.reduce((arr, mission) => {
                    if (mission.targetNum === state.StateScore.multiplier) {
                        arr.push(Actions.missionComplete(mission.uid));
                    }
                    return arr;
                }, []);
            }
            return [];
        })
    );
};

const missionEpic = combineEpics(
    matchMission,
    collectStars,
    collectToys,
    completeLevels,
    useHint,
    useFirecracker,
    useMagnet,
    updateMissions,
    beatLevel,
    reachCombo,
    initMissions
);

export default missionEpic;
